import { useCallback, useEffect, useState } from 'react';
import { ErrorMappingValueType } from 'typings';
import { MAX_FILE_SIZE } from 'constants/index';
import { Form, notification, Upload } from 'components';
import {
  useAttachmentCreateMutation,
  useAttachmentFileCreateMutation,
  useAttachmentTypeQuery,
  useAttachmentUpdateMutation,
} from 'api';
import { IAddEditModal } from './index';

export const useAddEdit = (props: IAddEditModal) => {
  const { dataToEdit, entityName, entityId, onModalStateChange, modalState, service } = props;
  const [isSaveBtnDisabled, setIsSaveBtnDisabled] = useState(true);
  const [form] = Form.useForm();
  const { data: attachmentType = [] } = useAttachmentTypeQuery({ entityName, service });
  const [createAttachment, { isLoading: createIsLoading }] = useAttachmentCreateMutation();
  const [updateAttachment, { isLoading: updateIsLoading }] = useAttachmentUpdateMutation();
  const [createAttachmentFile, { isLoading: createFileIsLoading }] = useAttachmentFileCreateMutation();
  const [notificationApi, contextHolder] = notification.useNotification();

  const openNotificationWithIcon = useCallback(
    (messageContent: ErrorMappingValueType) => {
      const { type, message } = messageContent;
      notificationApi[type]({
        message: message,
      });
    },
    [notificationApi]
  );

  const handleFileSizeValidation = useCallback(
    (file: File) => {
      if (file.size > MAX_FILE_SIZE) {
        form.setFields([
          {
            name: 'attachment',
            errors: [`File exceeds the maximum size of 5 MB.`],
            value: [],
          },
        ]);
        setIsSaveBtnDisabled(true);
        return Upload.LIST_IGNORE; // Prevent adding the file
      } else {
        form.setFields([
          {
            name: 'attachment',
            errors: undefined,
            value: [file],
          },
        ]);
        setIsSaveBtnDisabled(false);
      }
      return false;
    },
    [form]
  );

  const handleErrors = useCallback(
    (error: any) => {
      if (!error?.data?.success && error?.data?.data) {
        Object.values(error.data.data).forEach((message) => {
          openNotificationWithIcon({
            type: 'error',
            message: message as string,
          });
        });
      } else {
        openNotificationWithIcon({
          type: 'error',
          message: 'Something went wrong!',
        });
      }
    },
    [openNotificationWithIcon]
  );

  const handleCancel = useCallback(() => {
    form.resetFields();
    onModalStateChange();
  }, [form, onModalStateChange]);

  const handleSubmit = useCallback(async () => {
    try {
      const { attachment, attachmentType } = form.getFieldsValue();
      const payload: any = {
        entityId: entityId,
        service,
        attachmentType,
        entityName,
      };
      const formData = new FormData();
      if (attachment?.length && entityId) {
        const file = attachment[0];
        formData.append('files', file.originFileObj, file.name);
        formData.append('serviceName', service?.toUpperCase() as string);
      }
      if (dataToEdit) {
        payload.id = dataToEdit.id;
        if (entityName === 'ADMIN') {
          payload.entityId = undefined;
        }
      }
      const response = await (dataToEdit ? updateAttachment : createAttachment)(payload);
      if (response?.data?.success) {
        if (formData.has('files') && !dataToEdit) {
          formData.append('attachmentId', response.data.data);
          const fileResponse = await createAttachmentFile(formData);
          if (fileResponse?.data?.success) {
            form.resetFields();
            openNotificationWithIcon({
              type: 'success',
              message: `Attachment was successfully ${dataToEdit ? 'updated' : 'created'}.`,
            });
            handleCancel();
          } else {
            handleErrors(fileResponse.error);
          }
          // TODO need to check for same failure as in delete
        } else {
          openNotificationWithIcon({
            type: 'success',
            message: `Attachment was successfully ${dataToEdit ? 'updated' : 'created'}.`,
          });
          handleCancel();
        }
      } else {
        handleErrors(response.error);
      }
    } catch (err) {
      handleErrors(err);
    }
  }, [
    form,
    createAttachmentFile,
    createAttachment,
    updateAttachment,
    entityId,
    handleCancel,
    openNotificationWithIcon,
    handleErrors,
    entityName,
    dataToEdit,
    service,
  ]);

  useEffect(() => {
    if (dataToEdit && modalState) {
      form.setFieldsValue(dataToEdit);
    }
  }, [dataToEdit, modalState, form]);
  return {
    contextHolder,
    form,
    handleSubmit,
    isLoading: createIsLoading || createFileIsLoading || updateIsLoading,
    attachmentType,
    handleFileSizeValidation,
    isSaveBtnDisabled,
    handleCancel,
  };
};
